import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import moment, { MomentInput } from 'moment';
import { FormsModule } from '@angular/forms';
import { ButtonModule, DialogModule, InputTextareaModule } from 'primeng';
import { VmeCommonModule } from '../../../vme-common.module';
import { VmePipesModule } from '../../../vme-pipes.module';
import { Message } from '../../../classes/Message';
import { VehicleRegComponent } from '../../vehicle-reg/vehicle-reg.component';
import { PillComponent } from '../../supplier-risk-management/components/pill/pill.component';
import { OrderBookingHealthCheckService } from '../../../services/order-booking-health-check.service';
import {
  IBookingIssue,
  IOrderBookingSummary,
  IOrderBookingTask,
} from '../../../interfaces/order-booking-health-check/order-booking-health-check';
import {
  OrderBookingCheckType,
  OrderBookingIssueClosureReason,
  OrderBookingIssueStatus,
} from '../../../enums/order-booking-health-check/order-booking-health-check.enums';

// One label/value pair shown on the card, giving the operator the booking data behind the issue.
interface IIssueDetail {
  label: string;
  value: string;
}

@Component({
  selector: 'booking-issue-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    VmeCommonModule,
    VmePipesModule,
    DialogModule,
    ButtonModule,
    InputTextareaModule,
    VehicleRegComponent,
    PillComponent,
  ],
  templateUrl: './booking-issue-card.component.html',
  styleUrls: ['./booking-issue-card.component.scss'],
})
export class BookingIssueCardComponent {
  @Input() issue: IBookingIssue;
  @Input() booking: IOrderBookingSummary;

  @Output() issueChanged = new EventEmitter<void>();

  protected readonly OrderBookingIssueStatus = OrderBookingIssueStatus;

  protected showDismissDialog = false;
  protected dismissalReason = '';
  protected saving = false;

  constructor(private orderBookingHealthCheckService: OrderBookingHealthCheckService) {}

  get task(): IOrderBookingTask {
    return this.booking?.tasks?.find((t) => t.taskId === this.issue.taskId);
  }

  // The issue is raised against the return leg, so the leg it conflicts with is the one before it.
  // Checkable tasks only, matching how the checker paired them - an ended movement sitting between
  // the two legs would otherwise name the wrong outbound.
  private get outboundLeg(): IOrderBookingTask {
    const tasks = (this.booking?.tasks ?? []).filter((t) => t.isCheckable);
    const index = tasks.findIndex((t) => t.taskId === this.issue.taskId);

    return index > 0 ? tasks[index - 1] : null;
  }

  // A resolved issue is not always one anybody fixed, so the pill names the reason.
  get statusLabel(): string {
    if (this.issue.status === OrderBookingIssueStatus.Open) {
      return 'Open';
    }

    if (this.issue.status === OrderBookingIssueStatus.Dismissed) {
      return 'Dismissed';
    }

    return this.issue.closureReason === OrderBookingIssueClosureReason.NoLongerCorrectable
      ? 'No Longer Correctable'
      : 'Resolved';
  }

  // Resolved covers two outcomes, so one nobody corrected stays neutral rather than going green.
  get statusColours(): { background: string; text: string } {
    switch (this.issue.status) {
      case OrderBookingIssueStatus.Open:
        return { background: 'bg-blue-50', text: 'text-blue-700' };
      case OrderBookingIssueStatus.Dismissed:
        return { background: 'bg-red-50', text: 'text-red-600' };
      default:
        return this.issue.closureReason === OrderBookingIssueClosureReason.NoLongerCorrectable
          ? { background: 'bg-gray-200', text: 'text-gray-700' }
          : { background: 'bg-green-100', text: 'text-green-800' };
    }
  }

  // The booking fields relevant to this particular issue, so the operator can see what is wrong
  // without opening the order.
  get details(): IIssueDetail[] {
    const task = this.task;
    if (!task) {
      return [];
    }

    switch (this.issue.orderBookingCheckId as OrderBookingCheckType) {
      case OrderBookingCheckType.IncompleteCollectionAddress:
      case OrderBookingCheckType.MissingCollectionContact:
        return [
          {
            label: 'Collection Address',
            value: this.addressLine(task.pickupLocation, task.pickupPostcode),
          },
          {
            label: 'Collection Contact',
            value: this.contactLine(task.pickupContactName, task.pickupPhone),
          },
        ];

      case OrderBookingCheckType.IncompleteDeliveryAddress:
      case OrderBookingCheckType.MissingDeliveryContact:
        return [
          {
            label: 'Delivery Address',
            value: this.addressLine(task.dropoffLocation, task.dropoffPostcode),
          },
          {
            label: 'Delivery Contact',
            value: this.contactLine(task.dropoffContactName, task.dropoffPhone),
          },
        ];

      case OrderBookingCheckType.MissingVehicleModel:
        return [
          { label: 'Vehicle Make', value: this.orDash(task.vehicleMake) },
          { label: 'Vehicle Model', value: this.orDash(task.vehicleModel) },
        ];

      case OrderBookingCheckType.MissingMoDelPin:
        return [
          { label: 'Vehicle', value: this.orDash(task.vehicleReg) },
          { label: 'MoDel PIN', value: this.orDash(task.moDelPin) },
        ];

      case OrderBookingCheckType.CollectionOutsideOpeningHours:
      case OrderBookingCheckType.CollectionBeforeEarliestTime:
      case OrderBookingCheckType.CollectionAfterLatestTime:
        return [
          {
            label: 'Scheduled Collection',
            value: this.windowLine(task.pickupEarliestDateTime, task.pickupLatestDateTime),
          },
          { label: 'Site Opening Hours', value: this.openingHoursLine(task, true) },
        ];

      case OrderBookingCheckType.DeliveryOutsideOpeningHours:
      case OrderBookingCheckType.DeliveryBeforeEarliestTime:
      case OrderBookingCheckType.DeliveryAfterLatestTime:
        return [
          {
            label: 'Scheduled Delivery',
            value: this.windowLine(task.dropoffEarliestDateTime, task.dropoffLatestDateTime),
          },
          { label: 'Site Opening Hours', value: this.openingHoursLine(task, false) },
        ];

      case OrderBookingCheckType.ExchangeNotSameWorkingDay: {
        // The mismatch is between the outbound leg's delivery and this leg's collection, so show
        // exactly those two dates rather than the legs' own collection dates.
        const outbound = this.outboundLeg;
        return [
          {
            label: 'Outbound Delivered',
            value: outbound
              ? `${outbound.dropoffLocation}, ${this.windowDays(
                  outbound.dropoffEarliestDateTime,
                  outbound.dropoffLatestDateTime,
                )}`
              : '—',
          },
          {
            label: 'Return Collected',
            value: `${task.pickupLocation}, ${this.windowDays(
              task.pickupEarliestDateTime,
              task.pickupLatestDateTime,
            )}`,
          },
        ];
      }

      default:
        return [];
    }
  }

  private orDash(value: string): string {
    return value && value.trim().length > 0 ? value : '—';
  }

  private addressLine(city: string, postcode: string): string {
    return this.orDash([city, postcode].filter((x) => x && x.trim().length).join(', '));
  }

  private contactLine(name: string, phone: string): string {
    return this.orDash([name, phone].filter((x) => x && x.trim().length).join(' · '));
  }

  // A window can span days, so name them - otherwise Wed 10:00 to Sat 15:00 reads as a five
  // hour window on one day, and the opening hours below look like they contradict it.
  private windowLine(earliest: string, latest: string): string {
    return this.isSameDay(earliest, latest)
      ? `${this.dayLabel(earliest)} ${this.time(earliest)} – ${this.time(latest)}`
      : `${this.dayLabel(earliest)} ${this.time(earliest)} – ${this.dayLabel(latest)} ${this.time(
          latest,
        )}`;
  }

  private isSameDay(a: MomentInput, b: MomentInput): boolean {
    return moment(a).isSame(b, 'day');
  }

  // "Tue, 26 Aug", as the rest of the page shows a date.
  private dayLabel(value: MomentInput): string {
    return moment(value).format('ddd, DD MMM');
  }

  // A booked window can span more than one day, so show the range when it does.
  private windowDays(from: string, to: string): string {
    return this.isSameDay(from, to)
      ? this.dayLabel(from)
      : `${this.dayLabel(from)} – ${this.dayLabel(to)}`;
  }

  private time(value: MomentInput): string {
    return moment(value).format('HH:mm');
  }

  // Opening hours are stored as minutes from midnight, per day of week, so a window spanning two
  // days shows the hours for both.
  private openingHoursLine(task: IOrderBookingTask, isPickup: boolean): string {
    const from = new Date(isPickup ? task.pickupEarliestDateTime : task.dropoffEarliestDateTime);
    const to = new Date(isPickup ? task.pickupLatestDateTime : task.dropoffLatestDateTime);

    const days = this.isSameDay(from, to) ? [from] : [from, to];

    return days
      .map((day) => {
        const prefix = days.length > 1 ? `${moment(day).format('ddd')} ` : '';

        const hours = task.siteOpeningHours?.find(
          (h) => h.isPickup === isPickup && h.dayOfWeek === day.getDay(),
        );

        // No row for the day means the site never set it, which the check does not treat as a
        // breach, so this must not claim one either.
        if (!hours) {
          return `${prefix}Not configured`;
        }

        if (hours.open == null || hours.close == null) {
          // A row with no hours on an otherwise configured site means the site is shut that day.
          const configuredElsewhere = task.siteOpeningHours?.some(
            (h) => h.isPickup === isPickup && h.open != null && h.close != null,
          );
          return `${prefix}${configuredElsewhere ? 'Closed (outside)' : 'Not configured'}`;
        }

        // Both ends of the window are tested against the day, matching the check itself. Marking
        // only the start hid the breach whenever it was the latest time that fell outside.
        const { open, close } = hours;
        const outside = [from, to]
          .filter((end) => this.isSameDay(end, day))
          .some((end) => {
            const minutes = end.getHours() * 60 + end.getMinutes();
            return minutes < open || minutes > close;
          });

        return (
          `${prefix}${this.minutesToTime(open)} - ${this.minutesToTime(close)}` +
          (outside ? ' (outside)' : '')
        );
      })
      .join(' · ');
  }

  private minutesToTime(minutes: number): string {
    const h = Math.floor(minutes / 60)
      .toString()
      .padStart(2, '0');
    const m = (minutes % 60).toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  // Opens the job screen in a new tab so the operator keeps their place in the queue. Done from
  // a button rather than an anchor because pButton only styles a real button element.
  protected openJobScreen() {
    window.open(`/app/jobscreen/${this.booking.jobId}`, '_blank');
  }

  protected openDismissDialog() {
    this.dismissalReason = '';
    this.showDismissDialog = true;
  }

  protected async confirmDismiss() {
    if (!this.dismissalReason?.trim()) {
      Message.Error('Please give a reason for dismissing this issue.');
      return;
    }

    this.saving = true;
    try {
      await this.orderBookingHealthCheckService.dismissIssue(
        this.issue.issueId,
        this.dismissalReason.trim(),
      );
      this.showDismissDialog = false;
      this.issueChanged.emit();
    } catch {
      Message.Error('Could not dismiss this issue. Please try again.');
    } finally {
      this.saving = false;
    }
  }
}
